import React, { useState, useEffect } from 'react';
import { adminApi } from '../../../services/api';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Users, DollarSign, TrendingUp, Calendar, CreditCard, Repeat, Zap, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { ACCENT, GREEN, BLUE, PURPLE, AMBER, Spinner, KpiCard } from './shared';
import { useTimelineEvents } from '../timeline/useTimelineEvents';
import { buildTimelineDots } from '../timeline/buildTimelineDots';
import { TimelineToggleBar } from '../timeline/TimelineToggleBar';
import { bucketDateYearMonth } from '../timeline/timelineUtils';

export function OverviewTab() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const timeline = useTimelineEvents();
  useEffect(() => {
    adminApi.get('/admin/analytics/business?period=month').then(r => setD(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  if (loading) return <Spinner />;
  const kpi = d?.kpi || {};
  const avg = d?.averages || {};
  return (
    <div className="space-y-6" data-testid="overview-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Clienti totali" value={kpi.total_customers} icon={Users} color={ACCENT} testid="kpi-total-customers" />
        <KpiCard label="Attivi (30gg)" value={kpi.active_30d} icon={Zap} color={GREEN} sub={`Churn: ${kpi.churn_rate}%`} testid="kpi-active" />
        <KpiCard label="LTV medio" value={`\u20AC${kpi.ltv}`} icon={DollarSign} color={BLUE} testid="kpi-ltv" />
        <KpiCard label="Ricavi totali" value={`\u20AC${kpi.total_revenue?.toLocaleString('it-IT')}`} icon={TrendingUp} color={PURPLE} testid="kpi-revenue" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Visite questo mese" value={d?.current_period?.visits} change={d?.visits_change_pct} icon={Calendar} testid="kpi-visits-month" />
        <KpiCard label="Ricavi questo mese" value={`\u20AC${d?.current_period?.revenue?.toLocaleString('it-IT')}`} change={d?.revenue_change_pct} icon={DollarSign} color={GREEN} testid="kpi-rev-month" />
        <KpiCard label="Scontrino medio" value={`\u20AC${avg.avg_receipt}`} icon={CreditCard} color={AMBER} testid="kpi-receipt" />
        <KpiCard label="Freq. media (30gg)" value={`${avg.avg_frequency}x`} icon={Repeat} color={PURPLE} sub={`${avg.visits_per_day} visite/giorno`} testid="kpi-freq" />
      </div>
      {d?.trend?.length > 0 && (
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <h3 className="font-semibold text-[#1A1A1A] mb-4">Trend visite e ricavi (ultimi 12 mesi)</h3>
          <TimelineToggleBar
            enabled={timeline.enabled}
            setEnabled={timeline.setEnabled}
            categories={timeline.categories}
            setCategories={timeline.setCategories}
            events={timeline.filtered}
            bucketFn={bucketDateYearMonth}
            chartData={d.trend}
            xKey="label"
            expanded={timelineOpen}
            setExpanded={setTimelineOpen}
          />
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={d.trend} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gVisits" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={ACCENT} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={ACCENT} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={GREEN} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={GREEN} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#888' }} />
              <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#888' }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#888' }} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Area yAxisId="left" type="monotone" dataKey="visits" stroke={ACCENT} fill="url(#gVisits)" strokeWidth={2} name="Visite" />
              <Area yAxisId="right" type="monotone" dataKey="revenue" stroke={GREEN} fill="url(#gRevenue)" strokeWidth={2} name="Ricavi (\u20AC)" />
              {buildTimelineDots({
                events: timeline.filtered,
                chartData: d.trend,
                xKey: 'label',
                yMax: Math.max(...d.trend.map(t => t.visits || 0), 1) * 1.05,
                bucketFn: bucketDateYearMonth,
              }).map((dot) => React.cloneElement(dot, { yAxisId: 'left' }))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard label="Punti emessi" value={kpi.points_emitted?.toLocaleString('it-IT')} icon={ArrowUpRight} color={GREEN} testid="kpi-pts-emitted" />
        <KpiCard label="Punti riscattati" value={kpi.points_redeemed?.toLocaleString('it-IT')} icon={ArrowDownRight} color="#EF4444" testid="kpi-pts-redeemed" />
        <KpiCard label="Punti in circolo" value={kpi.points_circulation?.toLocaleString('it-IT')} icon={Repeat} color={BLUE} testid="kpi-pts-circulation" />
      </div>
    </div>
  );
}
